import { useState, useMemo } from 'react';
import { getRiskStatusConfig, formatPercent, formatMs, formatNumber, formatReleaseDate, formatVersion } from '../../utils/formatters';

/**
 * VersionIntelligenceTable — Single unified table combining Google Play + Firebase data.
 * Columns: Version, Release Date, Platform, Risk Score, Crash-Free %, ANR Rate, Startup P90, Users
 * Clickable rows open drill-down. Sortable columns.
 */

const COLUMNS = [
  { key: 'app_version', label: 'Version', sortable: true },
  { key: 'release_date', label: 'Released', sortable: true },
  { key: 'risk_status', label: 'Status', sortable: true },
  { key: 'release_risk_score', label: 'Risk Score', sortable: true },
  { key: 'crash_free_pct', label: 'Crash-Free %', sortable: true },
  { key: 'anr_rate', label: 'ANR Rate', sortable: true },
  { key: 'cold_start_p90', label: 'Startup P90', sortable: true },
  { key: 'total_users', label: 'Users', sortable: true },
  { key: 'launch_readiness', label: 'Readiness', sortable: true },
];

function RiskScorePill({ score }) {
  if (score == null || isNaN(score)) return <span className="text-gray-600 text-xs">—</span>;
  const s = parseFloat(score);
  let color = 'bg-emerald-500/20 text-emerald-400';
  if (s < 60) color = 'bg-red-500/20 text-red-400';
  else if (s < 75) color = 'bg-amber-500/20 text-amber-400';
  else if (s < 90) color = 'bg-blue-500/20 text-blue-400';
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${color}`}>
      {Math.round(s)}
    </span>
  );
}

function StatusDot({ status }) {
  const config = getRiskStatusConfig(status);
  if (!config) return <span className="text-gray-600 text-xs">—</span>;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${config.bg} ${config.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
}

function ReadinessBadge({ readiness }) {
  if (!readiness) return <span className="text-gray-600 text-xs">—</span>;
  const map = {
    green: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    yellow: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    red: 'bg-red-500/15 text-red-400 border-red-500/30',
  };
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${map[readiness] || map.yellow}`}>
      {readiness.toUpperCase()}
    </span>
  );
}

export default function VersionIntelligenceTable({ versions = [], readiness = [], onSelectVersion }) {
  const [sortKey, setSortKey] = useState('release_date');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');

  // Merge version data with readiness data
  const merged = useMemo(() => {
    const readinessMap = {};
    readiness.forEach(r => {
      readinessMap[r.app_version || r.appVersion] = r;
    });

    return versions.map(v => {
      const ver = v.app_version || v.appVersion;
      const r = readinessMap[ver] || {};
      return {
        ...v,
        ...r,
        app_version: ver,
        release_date: v.release_date || r.release_date || v.first_seen,
        risk_status: v.risk_status || r.risk_status,
        release_risk_score: parseFloat(v.release_risk_score || r.release_risk_score) || null,
        crash_free_pct: parseFloat(v.crash_free_pct || r.crash_free_pct || v.avg_crash_free) || null,
        anr_rate: parseFloat(v.anr_rate || r.anr_rate || v.avg_anr_rate) || null,
        cold_start_p90: parseInt(v.cold_start_p90 || r.cold_start_p90 || v.avg_startup_p90) || null,
        total_users: parseInt(v.total_users || r.total_users || v.user_count) || 0,
        launch_readiness: r.launch_readiness || v.launch_readiness,
      };
    });
  }, [versions, readiness]);

  // Filter + sort
  const filtered = useMemo(() => {
    let list = merged;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(v => v.app_version?.toLowerCase().includes(q));
    }
    return list.sort((a, b) => {
      const mul = sortDir === 'asc' ? 1 : -1;
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return av.localeCompare(bv) * mul;
      return (av - bv) * mul;
    });
  }, [merged, search, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  if (versions.length === 0) {
    return (
      <div className="bg-surface-700/40 rounded-xl border border-surface-600/50 p-8 text-center text-gray-500 text-sm">
        No version data available
      </div>
    );
  }

  return (
    <div className="bg-surface-700/40 rounded-xl border border-surface-600/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-600/50">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-md bg-brand-500/20 flex items-center justify-center">
            <svg className="w-3 h-3 text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-200">Version Intelligence</h3>
          <span className="text-[10px] text-gray-600 bg-surface-600/50 px-1.5 py-0.5 rounded">
            {filtered.length} versions
          </span>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Filter version..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-surface-700 border border-surface-600 rounded-lg text-xs text-gray-300 px-3 py-1.5 w-40 focus:ring-1 focus:ring-brand-500 focus:border-brand-500 placeholder-gray-600"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-surface-600/50">
              {COLUMNS.map(col => (
                <th
                  key={col.key}
                  onClick={() => col.sortable && handleSort(col.key)}
                  className={`text-left py-2.5 px-3 text-[10px] text-gray-500 uppercase tracking-wider font-semibold whitespace-nowrap ${
                    col.sortable ? 'cursor-pointer hover:text-gray-300 select-none' : ''
                  }`}
                >
                  {col.label}
                  {sortKey === col.key && (
                    <span className="ml-1 text-brand-400">{sortDir === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((v, i) => (
              <tr
                key={v.app_version + '-' + i}
                onClick={() => onSelectVersion?.(v)}
                className="border-b border-surface-700/50 hover:bg-surface-700/40 cursor-pointer transition-colors group"
              >
                <td className="py-2.5 px-3">
                  <span className="font-mono font-semibold text-gray-200 group-hover:text-brand-400 transition-colors">
                    {formatVersion(v.app_version, v.version_code || v.versionCode)}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-xs text-gray-400 whitespace-nowrap">
                  {v.release_date ? formatReleaseDate(v.release_date) : '—'}
                </td>
                <td className="py-2.5 px-3">
                  <StatusDot status={v.risk_status} />
                </td>
                <td className="py-2.5 px-3">
                  <RiskScorePill score={v.release_risk_score} />
                </td>
                <td className="py-2.5 px-3">
                  {v.crash_free_pct != null ? (
                    <span className={`text-xs font-medium ${v.crash_free_pct >= 99 ? 'text-emerald-400' : v.crash_free_pct >= 95 ? 'text-amber-400' : 'text-red-400'}`}>
                      {v.crash_free_pct.toFixed(2)}%
                    </span>
                  ) : <span className="text-gray-600 text-xs">—</span>}
                </td>
                <td className="py-2.5 px-3">
                  {v.anr_rate != null ? (
                    <span className={`text-xs font-medium ${v.anr_rate <= 0.1 ? 'text-emerald-400' : v.anr_rate <= 0.47 ? 'text-amber-400' : 'text-red-400'}`}>
                      {v.anr_rate.toFixed(3)}%
                    </span>
                  ) : <span className="text-gray-600 text-xs">—</span>}
                </td>
                <td className="py-2.5 px-3">
                  {v.cold_start_p90 != null ? (
                    <span className={`text-xs font-medium ${v.cold_start_p90 < 3000 ? 'text-emerald-400' : v.cold_start_p90 < 5000 ? 'text-amber-400' : 'text-red-400'}`}>
                      {formatMs(v.cold_start_p90)}
                    </span>
                  ) : <span className="text-gray-600 text-xs">—</span>}
                </td>
                <td className="py-2.5 px-3 text-xs text-gray-400">
                  {v.total_users ? formatNumber(v.total_users) : '—'}
                </td>
                <td className="py-2.5 px-3">
                  <ReadinessBadge readiness={v.launch_readiness} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
